// src/api/apiCall.js
import { API_SECRET, BASE_URL } from "@/constants/constant";
import { MDX_QUERY_ENDPOINT } from "@/lib/mdxQuery";
import axios from "axios";
import { toast } from "sonner";
import { getAccessToken, clearSession } from "@/utils/authSession";

const PUBLIC_ENDPOINTS = ["/WPUser/login"];

// ⬅️ NEW: endpoint-wise AbortController registry
// key = "GET:/mdx/query/v2" ya "POST:/mdx/query/v2"
// value = AbortController of the currently in-flight call for that endpoint
const inflightRequests = new Map();

// ⬅️ NEW: endpoints jinke liye auto-cancel chahiye
// (same endpoint ki nayi call purani ko cancel kare)
const AUTO_CANCEL_ENDPOINTS = new Set([
  "/getCubeMetaWithEdges",
  MDX_QUERY_ENDPOINT,
]);

export const apiCall = async (
  method,
  endpoint,
  data = null,
  navigate = null,
  isFormData = false,
  showToast = true,
  isRefresh = false,
  responseType = null,
  customHeaders = null,
  returnFullResponse = false,
  signal = null
) => {
  const token = getAccessToken();
  const m = method.toLowerCase();

  // ⬅️ NEW: agar ye auto-cancel endpoint hai to purani same-endpoint call abort karo
  // NOTE: query string hata ke base path pe match karte hain, kyunki
  // /getCubeMetaWithEdges?server=X&cube=Y mein har baar query alag hoti hai
  const basePath = endpoint.split("?")[0];
  const key = `${m.toUpperCase()}:${basePath}`;

  let controller = null;

  if (AUTO_CANCEL_ENDPOINTS.has(basePath)) {
    // pehli wali same-endpoint request abort karo
    const prev = inflightRequests.get(key);
    if (prev) prev.abort();

    // naya controller banao
    controller = new AbortController();
    inflightRequests.set(key, controller);
  }

  if (!PUBLIC_ENDPOINTS.includes(endpoint)) {
    if (!token) {
      clearSession();
      if (navigate) {
        navigate("/login", { replace: true });
      } else {
        window.location.href = "/login";
      }
      toast.error("Session expired. Please login again.");
      return;
    }
  }

  try {
    const config = {
      method: m,
      url: `${BASE_URL}${endpoint}`,
      headers: {
        "Content-Type": isFormData ? "multipart/form-data" : "application/json",
        secret: API_SECRET,
        ...(customHeaders || {}),
      },
    };

    // external signal + internal auto-cancel signal dono support karo
    if (controller && signal) {
      // dono ko combine karo — jo pehle abort kare wahi jeete
      signal.addEventListener("abort", () => controller.abort(), { once: true });
      config.signal = controller.signal;
    } else if (controller) {
      config.signal = controller.signal;
    } else if (signal) {
      config.signal = signal;
    }

    if (token && !PUBLIC_ENDPOINTS.includes(endpoint)) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (isRefresh) {
      config.headers["X-Force-Refresh"] = "true";
    }

    if (["post", "put", "patch"].includes(m) && data) {
      config.data = data;
    }

    if (m === "get" && data) {
      config.params = data;
    }

    if (responseType) config.responseType = responseType;

    const response = await axios(config);

    // ⬅️ NEW: successfully complete hui to registry se hatao (agar wahi current hai)
    if (controller && inflightRequests.get(key) === controller) {
      inflightRequests.delete(key);
    }

    if (returnFullResponse) {
      return response;
    }

    if (showToast) {
      if (
        response?.data?.success === true ||
        response?.data?.success === "true" ||
        response?.data?.status === true ||
        response?.data?.status === "true" ||
        response?.data?.status === "SUCCESS"
      ) {
        if (m !== "get") {
          if (m === "post") {
            toast.success(response?.data?.message || "Data created successfully");
          } else if (m === "put") {
            toast.success(response?.data?.message || "Data updated successfully");
          } else if (m === "delete") {
            toast.success(response?.data?.message || "Data deleted successfully");
          }
        }
      } else {
        toast.error(response?.data?.message || "Something went wrong. Please try again.");
      }
    }

    return response?.data;
  } catch (error) {
    // ⬅️ NEW: registry se cleanup
    if (controller && inflightRequests.get(key) === controller) {
      inflightRequests.delete(key);
    }

    // ⬅️ NEW: aborted requests ko silently ignore karo
    if (
      axios.isCancel?.(error) ||
      error?.code === "ERR_CANCELED" ||
      error?.name === "CanceledError"
    ) {
      throw { __aborted: true };
    }

    if (!error.response) {
      toast.error("No internet connection. Please check your network.");
      throw "No internet connection. Please check your network.";
    }

    if (error?.response?.status === 401) {
      clearSession();
      if (navigate) {
        navigate("/login", { replace: true });
      } else {
        window.location.href = "/login";
      }
      toast.error(error?.response?.data?.message || "Session expired. Please login again.");
    }
    throw error.response?.data?.message || "Something went wrong. Please try again.";
  }
};